import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import EmbedPreview from '@/components/feature/EmbedPreview';
import {
  accepterCandidat,
  computeMissionPayout,
  fetchMissionCandidatures,
  fetchMissions,
  postulerMission,
  refuserCandidat,
  rejeterLivraison,
  soumettreLivraison,
  updateMissionStatut,
  validerLivraison,
  CANDIDATURE_STATUT_META,
  MISSION_PLATFORMS,
  MISSION_STATUT_META,
  type Mission,
  type MissionCandidature,
} from '@/lib/missions';

// ─────────────────────────────────────────────────────────────
// Détail d'une mission côté marchand (/dashboard/missions/:id).
//
// • Le propriétaire voit les candidatures, retient/refuse, vérifie les
//   publications (vidéo + captures + lien en mini-navigateur) et valide
//   les vues → le montant dû est calculé automatiquement.
// • Le candidat retenu dépose son lien de publication, ses captures et sa
//   petite vidéo, ainsi que les vues / likes / commentaires constatés.
// • Durée maximale d'une mission : 31 jours.
// ─────────────────────────────────────────────────────────────

const MAX_DAYS = 31;

/** Clés réservées dans `liens` pour stocker les preuves (aucune colonne en plus). */
const PROOF_KEYS = {
  j1: 'preuve_j1', j1v: 'preuve_j1_video', fin: 'preuve_fin', finv: 'preuve_fin_video',
  vues: 'preuve_vues', likes: 'preuve_likes', commentaires: 'preuve_commentaires',
} as const;

interface Proofs {
  j1: string;
  j1v: string;
  fin: string;
  finv: string;
  vues: number;
  likes: number;
  commentaires: number;
}

function readProofs(c: MissionCandidature | null): Proofs {
  const raw = (c?.liens || {}) as Record<string, string>;
  const num = (v: string | undefined) => Number(String(v || '').replace(/[^0-9]/g, '')) || 0;
  return {
    j1: raw[PROOF_KEYS.j1] || '',
    j1v: raw[PROOF_KEYS.j1v] || '',
    fin: raw[PROOF_KEYS.fin] || '',
    finv: raw[PROOF_KEYS.finv] || '',
    vues: num(raw[PROOF_KEYS.vues]),
    likes: num(raw[PROOF_KEYS.likes]),
    commentaires: num(raw[PROOF_KEYS.commentaires]),
  };
}

function daysLeft(dateLimite: string | null): number | null {
  if (!dateLimite) return null;
  const d = new Date(dateLimite).getTime();
  if (Number.isNaN(d)) return null;
  return Math.ceil((d - Date.now()) / 86400000);
}

function canEmbed(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export default function MissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const missionId = Number(id);

  const [mission, setMission] = useState<Mission | null>(null);
  const [candidatures, setCandidatures] = useState<MissionCandidature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  // Mini-navigateur du contenu à partager
  const [browserUrl, setBrowserUrl] = useState('');
  const [browserInput, setBrowserInput] = useState('');

  // Candidature (côté candidat)
  const [lienSocial, setLienSocial] = useState('');
  const [abonnes, setAbonnes] = useState('');
  const [message, setMessage] = useState('');

  // Livraison (côté candidat retenu)
  const [lienPub, setLienPub] = useState('');
  const [proofs, setProofs] = useState<Proofs>({ j1: '', j1v: '', fin: '', finv: '', vues: 0, likes: 0, commentaires: 0 });
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const pendingSlot = useRef<keyof Proofs | null>(null);

  // Validation (côté propriétaire)
  const [vuesInput, setVuesInput] = useState<Record<number, string>>({});
  // Aperçu intégré de la publication, directement dans la carte du candidat.
  const [inlinePreviewId, setInlinePreviewId] = useState<number | null>(null);

  const myId = Number(user?.id || 0);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 4500);
  };

  const load = useCallback(async () => {
    if (!Number.isFinite(missionId)) {
      setError('Mission introuvable.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const all = await fetchMissions();
      const found = all.find((m) => m.id === missionId) || null;
      setMission(found);
      if (found) {
        setBrowserUrl(found.lien_contenu || '');
        setBrowserInput(found.lien_contenu || '');
        const rows = await fetchMissionCandidatures([found.id]);
        setCandidatures(rows);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger la mission.');
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => { void load(); }, [load]);

  const isOwner = !!mission && Number(mission.user_id) === myId;
  const myCandidature = useMemo(
    () => candidatures.find((c) => c.user_id === myId) || null,
    [candidatures, myId],
  );

  useEffect(() => {
    if (!myCandidature) return;
    setLienPub(myCandidature.lien_publication || '');
    setProofs(readProofs(myCandidature));
  }, [myCandidature?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Applique un morceau de preuve au candidat courant. */
  const patchLiens = async (patch: Record<string, string>) => {
    if (!myCandidature) return;
    const next = { ...(myCandidature.liens || {}), ...patch };
    setCandidatures((prev) => prev.map((c) => (c.id === myCandidature.id ? { ...c, liens: next } : c)));
    const { supabase } = await import('@/lib/supabase');
    await supabase.from('mission_candidatures').update({ liens: next, updated_at: new Date().toISOString() }).eq('id', myCandidature.id);
  };

  const pickProof = (slot: keyof Proofs) => {
    pendingSlot.current = slot;
    fileRef.current?.click();
  };

  const onProofFile = async (file: File | undefined) => {
    const slot = pendingSlot.current;
    if (!file || !slot) return;
    setUploadingKey(slot);
    try {
      const url = await uploadMediaFile(file, 'missions');
      if (!url) throw new Error('upload');
      setProofs((prev) => ({ ...prev, [slot]: url }));
      await patchLiens({ [PROOF_KEYS[slot as keyof typeof PROOF_KEYS] || String(slot)]: url });
      showToast('Preuve ajoutée.');
    } catch {
      showToast("Impossible d'envoyer ce fichier.");
    } finally {
      setUploadingKey(null);
      pendingSlot.current = null;
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const apply = async () => {
    if (!mission) return;
    if (!lienSocial.trim()) {
      showToast('Ajoutez le lien de votre profil.');
      return;
    }
    setBusy(true);
    try {
      await postulerMission(
        mission,
        { id: myId, nom: user?.nomcommerce || user?.name || user?.email || 'Membre', email: user?.email || null },
        { liens: { [String(mission.plateformes?.[0] || 'instagram')]: lienSocial.trim() }, abonnes: { total: Number(abonnes) || 0 }, message },
      );
      setMessage('');
      showToast('Candidature envoyée.');
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  };

  const submitPublication = async () => {
    if (!mission || !myCandidature) return;
    if (!lienPub.trim()) {
      showToast('Ajoutez le lien de votre publication.');
      return;
    }
    setBusy(true);
    try {
      await soumettreLivraison(mission, myCandidature, lienPub, proofs.vues);
      await patchLiens({
        [PROOF_KEYS.vues]: String(proofs.vues),
        [PROOF_KEYS.likes]: String(proofs.likes),
        [PROOF_KEYS.commentaires]: String(proofs.commentaires),
      });
      showToast('Publication envoyée pour vérification.');
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  };

  const validate = async (c: MissionCandidature) => {
    if (!mission) return;
    const vues = Number(String(vuesInput[c.id] || c.vues_declarees || 0).replace(/[^0-9]/g, '')) || 0;
    setBusy(true);
    try {
      const montant = await validerLivraison(mission, c, vues);
      showToast(`Validé : ${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${mission.monaie || 'MAD'} dus.`);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Validation impossible.');
    } finally {
      setBusy(false);
    }
  };

  // ── Chargement / erreur ──
  if (loading) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !mission) {
    return (
      <div className="p-4 md:p-6">
        <div className="max-w-md mx-auto text-center py-20">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-base font-semibold text-foreground-900 mb-1">Mission introuvable</h2>
          <p className="text-sm text-foreground-500 mb-5">{error || "Cette mission n'existe plus."}</p>
          <Link to="/dashboard/missions" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline">
            <i className="ri-arrow-left-line"></i>Retour aux missions
          </Link>
        </div>
      </div>
    );
  }

  const statut = MISSION_STATUT_META[mission.statut] || { label: mission.statut, className: 'bg-background-200/70 text-foreground-600' };
  const left = daysLeft(mission.date_limite);
  const tarif = Number(mission.tarif_1000_vues) || 0;
  const devise = mission.monaie || 'MAD';
  const tarifAffiche = `${tarif.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise} / 1000 vues`;
  const pending = candidatures.filter((c) => c.statut === 'en_attente');
  const retained = candidatures.filter((c) => c.statut !== 'en_attente' && c.statut !== 'refusee');
  const totalDu = retained.reduce((sum, c) => sum + (Number(c.montant_du) || 0), 0);

  return (
    <div className="p-4 md:p-6">
      {/* Fil d'ariane */}
      <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
        <Link to="/dashboard/missions" className="hover:text-foreground-800 no-underline flex items-center gap-1">
          <i className="ri-arrow-left-line"></i>Missions
        </Link>
        <span className="text-foreground-300">/</span>
        <span className="text-foreground-700 font-medium truncate max-w-[280px]">{mission.titre}</span>
      </div>

      {/* En-tête */}
      <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${statut.className}`}>{statut.label}</span>
              {left != null && (
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${left < 0 ? 'bg-red-50 text-red-600' : left <= 3 ? 'bg-amber-50 text-amber-700' : 'bg-secondary-100 text-secondary-900'}`}>
                  <i className="ri-time-line mr-1"></i>
                  {left < 0 ? 'Échéance dépassée' : `${left} jour${left > 1 ? 's' : ''} restant${left > 1 ? 's' : ''}`}
                </span>
              )}
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-600">
                Durée max {MAX_DAYS} jours
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 leading-tight">{mission.titre}</h1>
            <p className="text-xs text-foreground-500 mt-1 flex items-center gap-2 flex-wrap">
              <span><i className="ri-store-2-line mr-1"></i>{mission.auteur_nom || 'Commerce'}</span>
              {(mission.ville || mission.pays) && (
                <span><i className="ri-map-pin-line mr-1"></i>{[mission.ville, mission.pays].filter(Boolean).join(', ')}</span>
              )}
              <span className="font-semibold text-primary-700">{tarifAffiche}</span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {isOwner && mission.statut === 'ouverte' && (
              <button
                onClick={async () => { await updateMissionStatut(mission.id, 'fermee'); showToast('Candidatures fermées.'); await load(); }}
                className="px-4 py-2 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-secondary-600 transition-colors"
              >
                <i className="ri-lock-line mr-1"></i>Fermer les candidatures
              </button>
            )}
            {isOwner && mission.statut === 'fermee' && (
              <button
                onClick={async () => { await updateMissionStatut(mission.id, 'terminee'); showToast('Mission terminée.'); await load(); }}
                className="px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
              >
                <i className="ri-flag-line mr-1"></i>Terminer la mission
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard/missions')}
              className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
            >
              Toutes les missions
            </button>
          </div>
        </div>

        {isOwner && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-4">
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Candidatures</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{candidatures.length}</p>
            </div>
            <div className="rounded-lg bg-accent-50 p-3">
              <p className="text-[10px] uppercase tracking-wide text-accent-800 font-semibold">En attente</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{pending.length}</p>
            </div>
            <div className="rounded-lg bg-secondary-50 p-3">
              <p className="text-[10px] uppercase tracking-wide text-secondary-900 font-semibold">Retenus</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{retained.length}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Montant dû</p>
              <p className="text-sm font-bold font-heading text-foreground-950 leading-tight">
                {totalDu.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {devise}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4">
        {/* Colonne principale */}
        <div className="space-y-4 min-w-0">
          {/* Brief */}
          <section className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5">
            <h2 className="text-sm font-bold font-heading text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-file-text-line text-primary-500"></i>Brief de la mission
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
              {mission.description || 'Aucune description fournie.'}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4">
              <div className="rounded-lg bg-background-100 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Type</p>
                <p className="text-xs font-semibold text-foreground-800 mt-0.5">{mission.type_contenu || '—'}</p>
              </div>
              <div className="rounded-lg bg-background-100 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Vues visées</p>
                <p className="text-xs font-semibold text-foreground-800 mt-0.5">
                  {(Number(mission.vues_cibles) || 0).toLocaleString('fr-FR')}
                </p>
              </div>
              <div className="rounded-lg bg-background-100 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Musique</p>
                <p className="text-xs font-semibold text-foreground-800 mt-0.5">{mission.musique_autorisee ? 'Autorisée' : 'Non'}</p>
              </div>
              <div className="rounded-lg bg-background-100 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Échéance</p>
                <p className="text-xs font-semibold text-foreground-800 mt-0.5">
                  {mission.date_limite ? new Date(mission.date_limite).toLocaleDateString('fr-FR') : '—'}
                </p>
              </div>
            </div>

            {mission.plateformes && mission.plateformes.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-3">
                {mission.plateformes.map((key) => {
                  const meta = MISSION_PLATFORMS.find((p) => p.key === key);
                  return (
                    <span key={key} className="px-2 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium inline-flex items-center gap-1">
                      <i className={meta?.icon || 'ri-global-line'}></i>{meta?.label || key}
                    </span>
                  );
                })}
              </div>
            )}

            {mission.media && mission.media.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                {mission.media.map((m, i) => (
                  <div key={`${m.url}-${i}`} className="rounded-lg overflow-hidden border border-background-200/70 bg-background-100 aspect-[4/3]">
                    {String(m.type || '').startsWith('video') ? (
                      <video src={m.url} controls className="w-full h-full object-cover" />
                    ) : (
                      <img src={m.url} alt={`Média ${i + 1}`} className="w-full h-full object-cover" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Candidatures (propriétaire) */}
          {isOwner && (
            <section className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5">
              <h2 className="text-sm font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-user-follow-line text-primary-500"></i>Candidatures
                <span className="text-xs font-medium text-foreground-400">({candidatures.length})</span>
              </h2>

              {candidatures.length === 0 ? (
                <p className="text-sm text-foreground-500 py-6 text-center">Aucune candidature pour le moment.</p>
              ) : (
                <div className="space-y-2.5">
                  {candidatures.map((c) => {
                    const meta = CANDIDATURE_STATUT_META[c.statut] || { label: c.statut, className: 'bg-background-200/70 text-foreground-600' };
                    const proofsOfC = readProofs(c);
                    const abonnesTotal = Object.values(c.abonnes || {}).reduce((s, v) => s + (Number(v) || 0), 0);
                    return (
                      <div key={c.id} className="rounded-lg border border-background-200/70 bg-background-50 p-3">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground-900 truncate">{c.auteur_nom || `Candidat #${c.user_id}`}</p>
                            <p className="text-[11px] text-foreground-500">
                              {abonnesTotal > 0 ? `${abonnesTotal.toLocaleString('fr-FR')} abonnés` : 'Abonnés non précisés'}
                              {c.vues_declarees ? ` · ${Number(c.vues_declarees).toLocaleString('fr-FR')} vues` : ''}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                        </div>

                        {c.message && <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{c.message}</p>}

                        {/* Liens du profil */}
                        {c.liens && (
                          <div className="flex items-center gap-1.5 flex-wrap mt-2">
                            {Object.entries(c.liens)
                              .filter(([k, v]) => !!v && !k.startsWith('preuve_'))
                              .map(([k, v]) => (
                                <button
                                  key={k}
                                  onClick={() => { setBrowserUrl(v); setBrowserInput(v); }}
                                  className="px-2 py-1 rounded-full bg-background-100 text-foreground-700 text-[10px] font-medium hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                                  title="Voir dans le mini-navigateur"
                                >
                                  <i className="ri-window-line mr-1"></i>{k}
                                </button>
                              ))}
                          </div>
                        )}

                        {/* Preuves */}
                        {(proofsOfC.j1 || proofsOfC.j1v || proofsOfC.fin || proofsOfC.finv) && (
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                            {[
                              { url: proofsOfC.j1, label: 'Jour 1 — capture' },
                              { url: proofsOfC.j1v, label: 'Jour 1 — vidéo' },
                              { url: proofsOfC.fin, label: 'Fin — capture' },
                              { url: proofsOfC.finv, label: 'Fin — vidéo' },
                            ].filter((p) => !!p.url).map((p) => (
                              <button
                                key={p.label}
                                onClick={() => { setBrowserUrl(p.url); setBrowserInput(p.url); }}
                                className="rounded-md overflow-hidden border border-background-200/70 bg-background-100 aspect-video relative group cursor-pointer"
                                title={p.label}
                              >
                                {/\.(mp4|webm|mov)(\?|$)/i.test(p.url) ? (
                                  <video src={p.url} className="w-full h-full object-cover" />
                                ) : (
                                  <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                                )}
                                <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[9px] py-0.5 px-1 truncate">{p.label}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        {(proofsOfC.vues > 0 || proofsOfC.likes > 0 || proofsOfC.commentaires > 0) && (
                          <div className="flex items-center gap-2 flex-wrap mt-2 text-[11px] text-foreground-600">
                            <span><i className="ri-eye-line mr-1"></i>{proofsOfC.vues.toLocaleString('fr-FR')}</span>
                            <span><i className="ri-heart-3-line mr-1"></i>{proofsOfC.likes.toLocaleString('fr-FR')}</span>
                            <span><i className="ri-chat-3-line mr-1"></i>{proofsOfC.commentaires.toLocaleString('fr-FR')}</span>
                          </div>
                        )}

                        {/* Publication livrée : lien + aperçu INTÉGRÉ dans la carte */}
                        {c.lien_publication && (
                          <div className="mt-2 rounded-lg border border-background-200/60 bg-background-100/50 p-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <a
                                href={c.lien_publication}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-[11px] font-semibold no-underline whitespace-nowrap hover:bg-primary-100 transition-colors"
                              >
                                <i className="ri-external-link-line"></i>
                                Voir la publication
                              </a>
                              <button
                                type="button"
                                onClick={() => setInlinePreviewId(inlinePreviewId === c.id ? null : c.id)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-50 border border-background-200/70 text-foreground-700 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/60 transition-colors"
                                title="Afficher la publication directement dans la carte"
                              >
                                <i className="ri-window-line"></i>
                                {inlinePreviewId === c.id ? "Masquer l'aperçu" : 'Aperçu intégré'}
                              </button>
                              <span className="text-[10px] text-foreground-400 truncate max-w-[180px]">{c.lien_publication}</span>
                            </div>

                            {inlinePreviewId === c.id && (
                              <div className="mt-2 rounded-lg overflow-hidden border border-background-200/70 bg-background-100 aspect-video">
                                <EmbedPreview
                                  url={c.lien_publication}
                                  title={`Publication de ${c.auteur_nom || 'ce candidat'}`}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                          {c.statut === 'en_attente' && (
                            <>
                              <button
                                disabled={busy}
                                onClick={async () => { await accepterCandidat(mission, c); showToast('Candidat retenu.'); await load(); }}
                                className="px-3 py-1.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 disabled:opacity-50"
                              >
                                <i className="ri-check-line mr-1"></i>Retenir
                              </button>
                              <button
                                disabled={busy}
                                onClick={async () => { await refuserCandidat(mission, c); await load(); }}
                                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50"
                              >
                                Refuser
                              </button>
                            </>
                          )}

                          {c.statut === 'livree' && (
                            <div className="flex items-center gap-1.5 flex-wrap w-full">
                              <input
                                value={vuesInput[c.id] ?? String(c.vues_declarees || '')}
                                onChange={(e) => setVuesInput((p) => ({ ...p, [c.id]: e.target.value.replace(/[^0-9]/g, '') }))}
                                placeholder="Vues vérifiées"
                                inputMode="numeric"
                                className="w-32 px-2.5 py-1.5 rounded-lg border border-background-200/70 bg-background-100 text-xs text-foreground-900 focus:outline-none focus:border-primary-300"
                              />
                              <span className="text-[11px] text-foreground-500">
                                = {computeMissionPayout(mission.tarif_1000_vues, Number(vuesInput[c.id] ?? c.vues_declarees ?? 0)).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {devise}
                              </span>
                              <button
                                disabled={busy}
                                onClick={() => validate(c)}
                                className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50"
                              >
                                <i className="ri-check-double-line mr-1"></i>Valider
                              </button>
                              <button
                                disabled={busy}
                                onClick={async () => { await rejeterLivraison(mission, c, 'Preuves insuffisantes'); await load(); }}
                                className="px-3 py-1.5 rounded-full bg-background-100 text-red-600 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-red-50 disabled:opacity-50"
                              >
                                Rejeter
                              </button>
                            </div>
                          )}

                          {c.statut === 'validee' && (
                            <span className="text-[11px] font-semibold text-accent-700">
                              <i className="ri-check-double-line mr-1"></i>
                              {Number(c.montant_du || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {devise} validés
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Espace candidat */}
          {!isOwner && (
            <section className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5">
              <h2 className="text-sm font-bold font-heading text-foreground-950 mb-1 flex items-center gap-2">
                <i className="ri-send-plane-line text-primary-500"></i>
                {myCandidature ? 'Ma participation' : 'Postuler à cette mission'}
              </h2>

              {!myCandidature && (
                <div className="space-y-3 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Lien de mon profil</label>
                    <input
                      value={lienSocial}
                      onChange={(e) => setLienSocial(e.target.value)}
                      placeholder="https://www.tiktok.com/@vous"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Nombre d&apos;abonnés</label>
                    <input
                      value={abonnes}
                      onChange={(e) => setAbonnes(e.target.value.replace(/[^0-9]/g, ''))}
                      inputMode="numeric"
                      placeholder="ex. 12000"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Message (optionnel)</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                      placeholder="Comment comptez-vous réaliser la mission ?"
                    />
                  </div>
                  <button
                    onClick={apply}
                    disabled={busy || mission.statut !== 'ouverte'}
                    className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                  >
                    {mission.statut !== 'ouverte' ? 'Candidatures fermées' : busy ? 'Envoi…' : 'Envoyer ma candidature'}
                  </button>
                </div>
              )}

              {myCandidature && (
                <div className="space-y-3 mt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${(CANDIDATURE_STATUT_META[myCandidature.statut] || {}).className || 'bg-background-200/70 text-foreground-600'}`}>
                      {(CANDIDATURE_STATUT_META[myCandidature.statut] || {}).label || myCandidature.statut}
                    </span>
                    {myCandidature.montant_du != null && Number(myCandidature.montant_du) > 0 && (
                      <span className="text-[11px] font-semibold text-accent-700">
                        {Number(myCandidature.montant_du).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {devise}
                      </span>
                    )}
                  </div>

                  {(myCandidature.statut === 'acceptee' || myCandidature.statut === 'livree' || myCandidature.statut === 'rejetee') && (
                    <>
                      <p className="text-[11px] text-foreground-500 leading-relaxed">
                        Déposez la capture + petite vidéo de la publication <strong>le premier jour</strong>, puis à la fin de la mission
                        (captures + vidéo + nombre de vues, likes et commentaires).
                      </p>

                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1">Lien de ma publication</label>
                        <input
                          value={lienPub}
                          onChange={(e) => setLienPub(e.target.value)}
                          placeholder="https://…"
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { slot: 'j1' as const, label: 'Jour 1 — capture' },
                          { slot: 'j1v' as const, label: 'Jour 1 — vidéo' },
                          { slot: 'fin' as const, label: 'Fin — capture' },
                          { slot: 'finv' as const, label: 'Fin — vidéo' },
                        ]).map((item) => {
                          const url = proofs[item.slot] as string;
                          return (
                            <button
                              key={item.slot}
                              onClick={() => pickProof(item.slot)}
                              className="rounded-lg border border-dashed border-background-300/60 bg-background-100 p-2 text-left hover:border-primary-300 transition-colors cursor-pointer min-w-0"
                            >
                              <span className="block text-[10px] font-semibold text-foreground-500 truncate">{item.label}</span>
                              {uploadingKey === item.slot ? (
                                <span className="text-[11px] text-primary-600 flex items-center gap-1 mt-1"><i className="ri-loader-4-line animate-spin"></i>Envoi…</span>
                              ) : url ? (
                                <span className="text-[11px] text-accent-700 flex items-center gap-1 mt-1"><i className="ri-check-line"></i>Ajouté</span>
                              ) : (
                                <span className="text-[11px] text-foreground-400 flex items-center gap-1 mt-1"><i className="ri-upload-2-line"></i>Ajouter</span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        {([
                          { key: 'vues' as const, label: 'Vues' },
                          { key: 'likes' as const, label: 'Likes' },
                          { key: 'commentaires' as const, label: 'Commentaires' },
                        ]).map((f) => (
                          <div key={f.key}>
                            <label className="block text-[10px] font-semibold text-foreground-500 mb-1">{f.label}</label>
                            <input
                              value={String(proofs[f.key] || '')}
                              onChange={(e) => setProofs((p) => ({ ...p, [f.key]: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 }))}
                              inputMode="numeric"
                              className="w-full px-2 py-2 rounded-lg border border-background-200/70 bg-background-100 text-xs text-foreground-900 focus:outline-none focus:border-primary-300"
                            />
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={submitPublication}
                        disabled={busy}
                        className="w-full py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors"
                      >
                        <i className="ri-send-plane-line mr-1"></i>Envoyer ma publication
                      </button>
                    </>
                  )}
                </div>
              )}
            </section>
          )}
        </div>

        {/* Mini-navigateur : affiche le contenu / les preuves dans la page */}
        <aside className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden h-fit xl:sticky xl:top-20">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-background-200/70 bg-background-100">
            <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0"></span>
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
            <span className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0"></span>
            <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wide ml-1.5">Mini-navigateur</span>
          </div>

          <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-background-200/70">
            <i className="ri-lock-line text-xs text-foreground-400 flex-shrink-0"></i>
            <input
              value={browserInput}
              onChange={(e) => setBrowserInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canEmbed(browserInput)) setBrowserUrl(browserInput); }}
              placeholder="Collez un lien à afficher ici…"
              className="flex-1 min-w-0 px-2 py-1.5 rounded-md bg-background-100 border border-background-200/70 text-[11px] text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
            <button
              onClick={() => { if (canEmbed(browserInput)) setBrowserUrl(browserInput); }}
              className="w-7 h-7 rounded-md bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-primary-600 transition-colors"
              title="Ouvrir dans le mini-navigateur"
            >
              <i className="ri-arrow-right-line text-xs"></i>
            </button>
          </div>

          <div className="aspect-[4/3] bg-background-100">
            {browserUrl ? (
              <EmbedPreview url={browserUrl} title="Contenu de la mission" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center">
                <i className="ri-window-line text-2xl text-foreground-300 mb-1"></i>
                <p className="text-[11px] text-foreground-400">
                  Le contenu de la mission et les preuves s&apos;ouvrent ici, sans quitter la page.
                </p>
              </div>
            )}
          </div>

          {browserUrl && (
            <div className="px-3 py-2 border-t border-background-200/70 flex items-center justify-between gap-2">
              <span className="text-[10px] text-foreground-400 truncate">{browserUrl}</span>
              <a
                href={browserUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap no-underline"
              >
                Nouvel onglet <i className="ri-external-link-line"></i>
              </a>
            </div>
          )}

          <p className="px-3 py-2 text-[10px] text-foreground-400 leading-relaxed border-t border-background-200/70">
            Les publications Facebook, Instagram, TikTok et YouTube s&apos;affichent via leur lecteur officiel.
            Un profil ou un autre site s&apos;ouvre avec « Nouvel onglet ».
          </p>
        </aside>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => onProofFile(e.target.files?.[0])}
      />

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[60] w-[calc(100vw-2rem)] max-w-md">
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-xs font-medium">
            <i className="ri-information-line mt-0.5"></i>
            <span className="flex-1">{toast}</span>
          </div>
        </div>
      )}
    </div>
  );
}